/**
 * Employee Employment Tab Component
 * 
 * This component is extracted from EmployeeForm.jsx for modularity.
 * It maintains the exact same structure and data-testid attributes.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import { PositionListEditor } from './PositionListEditor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const EmploymentTab = ({ formData, handleChange, locations, branches, orgTree = [], orgLevels = [] }) => {
  // Tiefere Org-Ebenen (unterhalb des Standorts) für die optionale Zuordnung.
  const hasDeeperLevels = (orgLevels || []).length > 2;
  const flattenDescendants = (nodes, targetId, found = false, depth = 0, acc = []) => {
    for (const n of nodes || []) {
      const isTarget = n.id === targetId;
      if (found) acc.push({ id: n.id, name: n.name, depth, levelLabel: (orgLevels[n.level_index]?.label) || '' });
      flattenDescendants(n.children, targetId, found || isTarget, found ? depth + 1 : 0, acc);
    }
    return acc;
  };
  const locUnitId = formData.location_id ? `ou-loc-${formData.location_id}` : null;
  const deeperUnits = locUnitId ? flattenDescendants(orgTree, locUnitId) : [];

  return (
    <Card className="border-border/60">
      <CardHeader>
        <CardTitle className="font-heading">Beschäftigungsdaten</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="position">Einsatz als (Hauptfunktion)</Label>
            <Input
              id="position"
              value={formData.position || ''}
              onChange={(e) => handleChange('position', e.target.value)}
              data-testid="employee-position"
              placeholder="z. B. Anleiter:in"
            />
            <p className="text-xs text-muted-foreground">
              Wird als Standard für alle Einsätze dieses Mitarbeiters verwendet.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="position-list">Weitere Funktionen</Label>
            <PositionListEditor
              positionList={formData.position_list || []}
              primaryPosition={formData.position}
              onChange={(list) => handleChange('position_list', list)}
            />
            <p className="text-xs text-muted-foreground">
              Zusätzliche Funktionen, in denen dieser Mitarbeiter eingesetzt werden kann (z. B. SP-Fachkraft, Coach). Bei der Schicht-Anlage kannst du dann pro Tag die passende Funktion auswählen.
            </p>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="taetigkeitsart">Tätigkeitsart</Label>
            <div className="flex flex-col gap-3 sm:flex-row sm:gap-6" data-testid="employee-taetigkeitsart">
              {[
                { v: 'operativ', label: 'In Maßnahmen tätig (operativ)' },
                { v: 'verwaltung', label: 'Verwaltung / Overhead' },
              ].map((opt) => {
                const arts = Array.isArray(formData.taetigkeitsart) ? formData.taetigkeitsart : [];
                const checked = arts.includes(opt.v);
                return (
                  <label
                    key={opt.v}
                    className="flex items-center gap-2 cursor-pointer text-sm"
                    data-testid={`taetigkeitsart-option-${opt.v}`}
                  >
                    <Switch
                      checked={checked}
                      onCheckedChange={(on) => {
                        const next = on
                          ? [...arts.filter((a) => a !== opt.v), opt.v]
                          : arts.filter((a) => a !== opt.v);
                        handleChange('taetigkeitsart', next);
                      }}
                      data-testid={`taetigkeitsart-switch-${opt.v}`}
                    />
                    <span>{opt.label}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Mehrfachauswahl möglich (z. B. operativ <em>und</em> verwaltung). Unterscheidet maßnahmenbezogene Mitarbeiter (z. B. Sozialpädagog:in, Lehrkraft, Ausbilder:in, Jobcoach) von Verwaltungs-/Overhead-Personal (z. B. Verwaltung, Hausmeister, Abrechnung). Steuert Filter, Einsatzplanung, Maßnahmen-Exporte und Auswertungen. Bleibt nichts ausgewählt, wird die Art automatisch aus der Funktion abgeleitet.
            </p>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="department">Maßnahme/Abteilung</Label>
            <Input
              id="department"
              value={formData.department || ''}
              onChange={(e) => handleChange('department', e.target.value)}
              placeholder="z. B. BaE, BvB, AVGS oder freie Beschreibung"
              data-testid="employee-department"
            />
            <p className="text-xs text-muted-foreground">
              Freitext — mehrere Einträge mit Komma trennen (werden in Listen als Badges angezeigt). Beispiele: <em>BaE, BvB, AVGS</em> oder <em>BaE Vertretung Mo+Mi</em>.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="location_id">Hauptstandort</Label>
            <Select value={formData.location_id} onValueChange={(v) => { handleChange('location_id', v); if (formData.org_unit_id) handleChange('org_unit_id', ''); }}>
              <SelectTrigger data-testid="employee-location">
                <SelectValue placeholder="Standort auswählen" />
              </SelectTrigger>
              <SelectContent>
                {branches.map((branch) => {
                  const branchLocations = locations.filter(l => l.branch_id === branch.id);
                  if (branchLocations.length === 0) return null;
                  return (
                    <React.Fragment key={branch.id}>
                      <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                        {branch.name}
                      </div>
                      {branchLocations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id} className="pl-6">
                          {loc.name}
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  );
                })}
                {locations.filter(l => !l.branch_id).map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {hasDeeperLevels && (
            <div className="space-y-2 md:col-span-2" data-testid="employee-org-unit-field">
              <Label htmlFor="org_unit_id">Organisationseinheit (tiefere Ebene, optional)</Label>
              <Select
                value={formData.org_unit_id || '__none__'}
                onValueChange={(v) => handleChange('org_unit_id', v === '__none__' ? '' : v)}
                disabled={!formData.location_id || deeperUnits.length === 0}
              >
                <SelectTrigger data-testid="employee-org-unit">
                  <SelectValue placeholder="Keine (am Standort)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Keine (am Standort)</SelectItem>
                  {deeperUnits.map((u) => (
                    <SelectItem key={u.id} value={u.id} style={{ paddingLeft: `${12 + u.depth * 14}px` }}>
                      {u.name}{u.levelLabel ? ` · ${u.levelLabel}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {!formData.location_id
                  ? 'Bitte zuerst einen Hauptstandort wählen.'
                  : deeperUnits.length === 0
                    ? 'Für diesen Standort sind noch keine tieferen Einheiten angelegt (Firmeneinstellungen → Organisationsstruktur).'
                    : 'Ordnet den Mitarbeiter einer Abteilung/einem Team unter dem Standort zu. Steuert die baumbasierte Freigabekette.'}
              </p>
            </div>
          )}
          
          {/* Zusätzliche Standorte */}
          <div className="space-y-2">
            <Label>Zusätzliche Standorte</Label>
            <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
              {locations.filter(loc => loc.id !== formData.location_id).length === 0 ? (
                <p className="text-sm text-muted-foreground">Keine weiteren Standorte verfügbar</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {branches.map((branch) => {
                    const branchLocations = locations.filter(
                      l => l.branch_id === branch.id && l.id !== formData.location_id
                    );
                    if (branchLocations.length === 0) return null;
                    return (
                      <React.Fragment key={branch.id}>
                        <div className="col-span-2 text-xs font-semibold text-muted-foreground mt-2 first:mt-0">
                          {branch.name}
                        </div>
                        {branchLocations.map((loc) => (
                          <label
                            key={loc.id}
                            className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 p-1 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={(formData.additional_location_ids || []).includes(loc.id)}
                              onChange={(e) => {
                                const current = formData.additional_location_ids || [];
                                if (e.target.checked) {
                                  handleChange('additional_location_ids', [...current, loc.id]);
                                } else {
                                  handleChange('additional_location_ids', current.filter(id => id !== loc.id));
                                }
                              }}
                              className="rounded border-gray-300"
                            />
                            {loc.name}
                          </label>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {locations.filter(l => !l.branch_id && l.id !== formData.location_id).map((loc) => (
                    <label
                      key={loc.id}
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={(formData.additional_location_ids || []).includes(loc.id)}
                        onChange={(e) => {
                          const current = formData.additional_location_ids || [];
                          if (e.target.checked) {
                            handleChange('additional_location_ids', [...current, loc.id]);
                          } else {
                            handleChange('additional_location_ids', current.filter(id => id !== loc.id));
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      {loc.name}
                    </label>
                  ))}
                </div>
              )}
              {(formData.additional_location_ids || []).length > 0 && (
                <div className="flex flex-wrap gap-1 pt-2 border-t mt-2">
                  {(formData.additional_location_ids || []).map(locId => {
                    const loc = locations.find(l => l.id === locId);
                    return loc ? (
                      <Badge key={locId} variant="secondary" className="text-xs">
                        {loc.name}
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="employment_start">Eintrittsdatum</Label>
            <DatePickerDE
              value={formData.employment_start}
              onChange={(v) => handleChange('employment_start', v)}
              placeholder="Eintrittsdatum wählen"
              testId="employee-start-date"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="employment_type">Beschäftigungsart</Label>
            <Select value={formData.employment_type} onValueChange={(v) => handleChange('employment_type', v)}>
              <SelectTrigger data-testid="employee-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Vollzeit">Vollzeit</SelectItem>
                <SelectItem value="Teilzeit">Teilzeit</SelectItem>
                <SelectItem value="Minijob">Minijob</SelectItem>
                <SelectItem value="Praktikum">Praktikum</SelectItem>
                <SelectItem value="Werkstudent">Werkstudent</SelectItem>
                <SelectItem value="Ausbildung">Ausbildung</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Befristung */}
          <div className="col-span-1 md:col-span-2 p-4 rounded-lg bg-secondary/30 space-y-4">
            <div className="flex items-center gap-3">
              <Switch
                id="is_temporary"
                checked={formData.is_temporary}
                onCheckedChange={(v) => handleChange('is_temporary', v)}
                data-testid="employee-is-temporary"
              />
              <Label htmlFor="is_temporary" className="font-medium">
                Befristetes Arbeitsverhältnis
              </Label>
            </div>
            {formData.is_temporary && (
              <div className="space-y-2">
                <Label htmlFor="employment_end">Befristet bis</Label>
                <DatePickerDE
                  value={formData.employment_end}
                  onChange={(v) => handleChange('employment_end', v)}
                  placeholder="Enddatum wählen"
                  testId="employee-end-date"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="working_time_model">Arbeitszeitmodell</Label>
            <Select 
              value={formData.working_time_model || 'weekly'} 
              onValueChange={(v) => handleChange('working_time_model', v)}
            >
              <SelectTrigger data-testid="employee-working-model">
                <SelectValue placeholder="Modell wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Wochenstunden (fix)</SelectItem>
                <SelectItem value="monthly">Monatsstunden (variabel)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.working_time_model === 'monthly' ? (
            <div className="space-y-2">
              <Label htmlFor="weekly_hours">Standard-Wochenstunden</Label>
              <Input
                id="weekly_hours"
                type="number"
                step="0.5"
                value={formData.weekly_hours ?? ''}
                onChange={(e) => handleChange('weekly_hours', e.target.value)}
                data-testid="employee-hours"
              />
              <p className="text-xs text-muted-foreground">
                Wird verwendet, wenn keine monatliche Arbeitszeit hinterlegt ist
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="weekly_hours">Wochenstunden</Label>
              <Input
                id="weekly_hours"
                type="number"
                step="0.5"
                value={formData.weekly_hours ?? ''}
                onChange={(e) => handleChange('weekly_hours', e.target.value)}
                data-testid="employee-hours"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="vacation_days_per_year">Urlaubstage/Jahr</Label>
            <Input
              id="vacation_days_per_year"
              type="number"
              value={formData.vacation_days_per_year ?? ''}
              onChange={(e) => handleChange('vacation_days_per_year', e.target.value)}
              data-testid="employee-vacation-days"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vacation_carryover">Resturlaub Vorjahr</Label>
            <Input
              id="vacation_carryover"
              type="number"
              min="0"
              value={formData.vacation_carryover || 0}
              onChange={(e) => handleChange('vacation_carryover', e.target.value)}
              data-testid="employee-vacation-carryover"
            />
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(v) => handleChange('is_active', v)}
              data-testid="employee-active"
            />
            <Label htmlFor="is_active">Mitarbeiter aktiv</Label>
          </div>

          {/* Stempel-Erinnerungen (pro Mitarbeiter) */}
          <div className="col-span-1 md:col-span-2 p-4 rounded-lg bg-secondary/30 space-y-3">
            <div className="flex items-center gap-3">
              <Switch
                id="reminder_enabled"
                checked={formData.reminder_enabled !== false}
                onCheckedChange={(v) => handleChange('reminder_enabled', v)}
                data-testid="employee-reminder-enabled"
              />
              <Label htmlFor="reminder_enabled" className="font-medium">
                Stempel-Erinnerungen aktiv
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Erinnert diesen Mitarbeiter ans Ein-/Ausstempeln, ausgerichtet an seinen
              durchschnittlichen Stempelzeiten. Urlaub und Abwesenheit werden automatisch ausgenommen.
            </p>
            {formData.reminder_enabled !== false && (
              <div className="space-y-2 pt-1">
                <Label htmlFor="reminder_threshold_minutes">Erinnern nach (Minuten) — individuell</Label>
                <Input
                  id="reminder_threshold_minutes"
                  type="number"
                  min="0"
                  max="120"
                  step="1"
                  value={formData.reminder_threshold_minutes ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    handleChange(
                      'reminder_threshold_minutes',
                      raw === '' ? null : Math.max(0, Math.min(120, parseInt(raw, 10) || 0)),
                    );
                  }}
                  placeholder="Firmen-Standard verwenden"
                  data-testid="employee-reminder-threshold"
                  className="md:max-w-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Leer lassen = firmenweiten Standard verwenden. Ein Wert (0–120 Min.) überschreibt
                  den Standard nur für diesen Mitarbeiter.
                </p>

                <Label htmlFor="pre_shift_reminder_minutes" className="pt-2 block">Vor Dienstbeginn erinnern (Minuten) — individuell</Label>
                <Input
                  id="pre_shift_reminder_minutes"
                  type="number"
                  min="0"
                  max="180"
                  step="1"
                  value={formData.pre_shift_reminder_minutes ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    handleChange(
                      'pre_shift_reminder_minutes',
                      raw === '' ? null : Math.max(0, Math.min(180, parseInt(raw, 10) || 0)),
                    );
                  }}
                  placeholder="Firmen-Standard verwenden"
                  data-testid="employee-pre-shift-reminder"
                  className="md:max-w-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Push-Erinnerung so viele Minuten vor dem geplanten Dienstbeginn (Dienstplan).
                  Leer lassen = firmenweiten Standard verwenden.
                </p>

                <Label htmlFor="end_shift_reminder_minutes" className="pt-2 block">Vor Feierabend erinnern (Minuten) — individuell</Label>
                <Input
                  id="end_shift_reminder_minutes"
                  type="number"
                  min="0"
                  max="180"
                  step="1"
                  value={formData.end_shift_reminder_minutes ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    handleChange(
                      'end_shift_reminder_minutes',
                      raw === '' ? null : Math.max(0, Math.min(180, parseInt(raw, 10) || 0)),
                    );
                  }}
                  placeholder="Firmen-Standard verwenden"
                  data-testid="employee-end-shift-reminder"
                  className="md:max-w-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Push-Erinnerung ans Ausstempeln so viele Minuten vor dem geplanten Dienstende.
                  Leer lassen = firmenweiten Standard verwenden.
                </p>
              </div>
            )}
          </div>

          {/* Stundenkonto (kumulierte Überstunden) */}
          <div className="col-span-1 md:col-span-2 p-4 rounded-lg bg-secondary/30 space-y-4">
            <div>
              <h4 className="font-medium">Stundenkonto (kumulierte Überstunden)</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Überstunden/Minusstunden werden über die Monate fortlaufend addiert. Hier kannst du
                den Saldo beim Umstieg aus einem Altsystem als Startwert hinterlegen.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="overtime_starting_balance">Startsaldo (Stunden)</Label>
                <Input
                  id="overtime_starting_balance"
                  type="number"
                  step="0.25"
                  value={formData.overtime_starting_balance ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    handleChange(
                      'overtime_starting_balance',
                      raw === '' ? null : parseFloat(raw),
                    );
                  }}
                  placeholder="z. B. 12.5 oder -4"
                  data-testid="employee-overtime-starting-balance"
                />
                <p className="text-xs text-muted-foreground">
                  Positiver Wert = Überstunden-Guthaben, negativer Wert = Minusstunden. Leer = 0.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="overtime_account_start">Stundenkonto geführt ab</Label>
                <DatePickerDE
                  value={formData.overtime_account_start}
                  onChange={(v) => handleChange('overtime_account_start', v)}
                  placeholder="Stichtag wählen (optional)"
                  testId="employee-overtime-account-start"
                />
                <p className="text-xs text-muted-foreground">
                  Ab diesem Monat wird kumuliert; der Startsaldo gilt als Saldo zum Stichtag.
                  Leer lassen = ab dem ersten erfassten Monat.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Dienstliche Kontaktdaten */}
        <div className="border-t pt-6">
          <h4 className="font-medium mb-4">Dienstliche Kontaktdaten</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="business_phone">Dienstl. Telefon</Label>
              <Input
                id="business_phone"
                value={formData.business_phone || ''}
                onChange={(e) => handleChange('business_phone', e.target.value)}
                placeholder="+49 123 456789"
                data-testid="employee-business-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="business_mobile">Dienstl. Mobil</Label>
              <Input
                id="business_mobile"
                value={formData.business_mobile || ''}
                onChange={(e) => handleChange('business_mobile', e.target.value)}
                placeholder="+49 170 1234567"
                data-testid="employee-business-mobile"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="business_email">Dienstliche E-Mail *</Label>
              <Input
                id="business_email"
                type="email"
                value={formData.business_email || ''}
                onChange={(e) => handleChange('business_email', e.target.value)}
                placeholder="max.mustermann@firma.de"
                data-testid="employee-business-email"
              />
              <p className="text-xs text-muted-foreground">Wird als Login verwendet</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmploymentTab;
